import React, { useContext, useState, useEffect } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Drawer,
  TextField,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import { BallkidContext } from "../context/BallkidContext";
import {
  getBallkidAssignments,
  publishAssignments,
  addBallkidComment,
  getBallkidComments,
  getFreikartenForBallkid,
  getAttendanceForBallkid,
  getCheckInOutData,
  getClothingStatus,
  exportAllData,
  setBallkidCanNet,
  getPersonalMessage,
  setPersonalMessage
} from "../firebaseService";
import { useAuth } from "../AuthContext";
import { useGroup } from "../context/GroupContext";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Link } from "react-router-dom";
import './buttonStyle.css';


const formatTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const parseDay = (dayStr) => {
  const match = dayStr.match(/(\d{1,2})\.\s*(\w+)/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  const [_, day, month] = match;
  const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  const monthIndex = months.findIndex(m => m.toLowerCase() === month.toLowerCase());
  return monthIndex >= 0 ? monthIndex * 31 + parseInt(day) : Number.MAX_SAFE_INTEGER;
};

const BKOverview = () => {
  const { ballkids, loading } = useContext(BallkidContext);
  const { user } = useAuth();
  const { activeGroup } = useGroup();
  const memberLabel = activeGroup?.memberLabel || "Mitglieder";
  const memberSingular = activeGroup?.memberSingular || "Mitglied";
  const groupSlug = activeGroup?.slug || "ballkinder";
  const [assignments, setAssignments] = useState({});
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [selectedBallkid, setSelectedBallkid] = useState(null);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [netStatus, setNetStatus] = useState({}); // ballkidId → boolean
  const [personalMsg, setPersonalMsg] = useState("");
  const [personalMsgSaved, setPersonalMsgSaved] = useState(false);

  const loadAndPublishAssignments = async () => {
    setLoadingAssignments(true);
    const newAssignments = {};
    await Promise.all(
      ballkids.map(async (ballkid) => {
        const kidAssignments = await getBallkidAssignments(ballkid.id);
        newAssignments[ballkid.id] = kidAssignments;
      })
    );
    setAssignments(newAssignments);
    await publishAssignments(newAssignments);
    setLoadingAssignments(false);
  };

  const handleRowClick = async (ballkid) => {
    setSelectedBallkid(ballkid);
    setDrawerOpen(true);

    const [loadedComments, kidAssignments, freikarten, attendance, checkIns, clothing, loadedPersonalMsg] = await Promise.all([
      getBallkidComments(ballkid.id),
      getBallkidAssignments(ballkid.id),
      getFreikartenForBallkid(ballkid.id),
      getAttendanceForBallkid(ballkid.id),
      getCheckInOutData(),
      getClothingStatus(),
      getPersonalMessage(ballkid.id)
    ]);

    const freikartenSorted = Object.entries(freikarten.days || {}).sort((a, b) => parseDay(a[0]) - parseDay(b[0]));
    const checkInsForKid = checkIns
      .filter((c) => c.ballkidId === ballkid.id)
      .sort((a, b) => parseDay(a.day) - parseDay(b.day));

    setComments(loadedComments);
    setPersonalMsg(loadedPersonalMsg);
    setPersonalMsgSaved(false);
    setDetailData({
      assignments: kidAssignments.sort((a, b) => parseDay(a.day) - parseDay(b.day)),
      freikarten: { ...freikarten, sortedDays: freikartenSorted },
      attendance: attendance.availability || {},
      checkIns: checkInsForKid,
      clothingReceived: clothing[ballkid.id] || false,
    });
  };

  const handleExportAllData = async () => {
      const allData = await exportAllData();
      const wb = new ExcelJS.Workbook();

      Object.entries(allData).forEach(([collectionName, records]) => {
        const ws = wb.addWorksheet(collectionName.slice(0, 31));
        if (records.length > 0) {
          ws.columns = Object.keys(records[0]).map((key) => ({ header: key, key }));
          records.forEach((record) => ws.addRow(record));
        }
      });

      const buffer = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), 'allBallkidData.xlsx');
    };

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    const newComment = {
      text: comment,
      timestamp: new Date().toISOString(),
      userEmail: user?.email || "Unbekannt",
    };

    await addBallkidComment(selectedBallkid.id, newComment);
    setComments([...comments, newComment]);
    setComment("");
  };

  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(memberLabel);
    ws.columns = [
      { header: "Name", key: "Name" },
      { header: "Link", key: "Link" },
    ];
    ballkids.forEach((ballkid) => {
      ws.addRow({ Name: ballkid.name, Link: `https://ballkid-management.de/${groupSlug}/member/${ballkid.id}` });
    });

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${memberLabel}_Uebersicht.xlsx`);
  };

  useEffect(() => {
    if (ballkids?.length > 0) {
      const initial = {};
      ballkids.forEach(bk => { initial[bk.id] = !!bk.canNet; });
      setNetStatus(initial);
    }
  }, [ballkids]);

  const handleNetToggle = async (e, ballkidId) => {
    e.stopPropagation();
    const newVal = !netStatus[ballkidId];
    setNetStatus(prev => ({ ...prev, [ballkidId]: newVal }));
    try {
      await setBallkidCanNet(ballkidId, newVal);
    } catch (error) {
      console.error("Fehler beim Speichern von canNet:", error);
      // Rollback bei Fehler
      setNetStatus(prev => ({ ...prev, [ballkidId]: !newVal }));
    }
  };

  if (loading) {
    return <h2>Laedt...</h2>;
  }
  if (!ballkids || ballkids.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>{memberLabel}-Uebersicht</Typography>
        <Typography color="text.secondary">
          Noch keine {memberLabel} vorhanden. Importiere {memberLabel} ueber die Admin-Tools.
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
        <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
          {memberLabel}-Uebersicht
        </Typography>
        <div className="button-container">
          <Button variant="contained" color="primary" onClick={loadAndPublishAssignments} disabled={loadingAssignments} className="button">
            {loadingAssignments ? "Laedt..." : "Alle Einteilungen veroeffentlichen"}
          </Button>
          <Button variant="contained" color="primary" component={Link} to="../all_assignments" className="button">
            Alle Einteilungen ansehen und drucken
          </Button>
          <Button variant="contained" color="secondary" onClick={handleExportAllData} className="button">
            ALLE DATEN EXPORTIEREN
          </Button>
        </div>

        <TableContainer component={Paper} style={{ marginTop: 20 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Netz</TableCell>
                <TableCell># Einsätze</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ballkids.map((ballkid) => (
                <TableRow key={ballkid.id} onClick={() => handleRowClick(ballkid)} style={{ cursor: "pointer" }}>
                  <TableCell>{ballkid.name}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()} sx={{ p: 0, textAlign: "center" }}>
                    <Checkbox
                      checked={!!netStatus[ballkid.id]}
                      onChange={(e) => handleNetToggle(e, ballkid.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{ballkid.jobs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Container style={{ width: 400, padding: 20 }}>
            <h2>{selectedBallkid?.name}</h2>

            {detailData && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }}>Einteilungen:</Typography>
<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
  {detailData.assignments.map((a, i) => (
    <Chip
      key={i}
      label={`${a.day}: ${a.court} (${a.crew})`}
      size="small"
      variant="outlined"
    />
  ))}
</Box>

                
                <List dense>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>Check-ins:</Typography>
              {detailData.checkIns.map((entry, i) => (
                <ListItem key={i} disableGutters>
                      <ListItemText
                        primary={<strong>{entry.day}</strong>}
                        secondary={`${formatTime(entry.checkIn)} – ${formatTime(entry.checkOut)}`}
                      />
                    </ListItem>
                  ))}
                </List>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }}>Freikarten:</Typography>
<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
  {detailData.freikarten.sortedDays
    .filter(([, count]) => count > 0)
    .map(([day, count]) => (
      <Chip
        key={day}
        label={`${day}: ${count}`}
        size="small"
        color="success"
        variant="outlined"
      />
    ))}
</Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }}>Verfügbarkeit:</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {Object.keys(detailData.attendance)
                    .filter((d) => d !== "availability" && d !== "comment" && detailData.attendance[d])
                    .sort((a, b) => parseDay(a) - parseDay(b))
                    .map((day) => (
                      <Chip key={day} label={day} size="small" />
                    ))}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2 }}>Kleidung:</Typography>
                <Typography variant="body2">
                  {detailData.clothingReceived ? "✅ erhalten" : "❌ offen"}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedBallkid?.canNet || false}
                      onChange={async (e) => {
                        const newVal = e.target.checked;
                        await setBallkidCanNet(selectedBallkid.id, newVal);
                        setSelectedBallkid({ ...selectedBallkid, canNet: newVal });
                        setNetStatus(prev => ({ ...prev, [selectedBallkid.id]: newVal }));
                      }}
                    />
                  }
                  label="Kann ans Netz"
                  sx={{ mt: 2, display: "block" }}
                />
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2 }}>Persoenliche Nachricht</Typography>
            <Typography variant="caption" color="text.secondary">
              Wird dem {memberSingular} auf seiner Seite angezeigt. Leer lassen = nicht anzeigen.
            </Typography>
            <TextField
              label={`Nachricht an ${selectedBallkid?.name?.split(" ")[0] || memberSingular}`}
              multiline
              rows={3}
              fullWidth
              value={personalMsg}
              onChange={(e) => { setPersonalMsg(e.target.value); setPersonalMsgSaved(false); }}
              variant="outlined"
              sx={{ mt: 1, mb: 1 }}
            />
            <Button
              variant="contained"
              color={personalMsgSaved ? "success" : "primary"}
              size="small"
              onClick={async () => {
                await setPersonalMessage(selectedBallkid.id, personalMsg);
                setPersonalMsgSaved(true);
              }}
            >
              {personalMsgSaved ? "Gespeichert" : "Nachricht speichern"}
            </Button>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>Kommentare</Typography>
            <TextField
              label="Neuer Kommentar"
              multiline
              rows={3}
              fullWidth
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              variant="outlined"
              style={{ marginBottom: 10 }}
            />
            <Button variant="contained" color="primary" onClick={handleAddComment}>
              Kommentar hinzufügen
            </Button>
            <div style={{ marginTop: 20 }}>
              {comments.map((c, index) => (
                <Paper key={index} style={{ padding: 10, marginBottom: 5 }}>
                  <Typography variant="body2">{c.text}</Typography>
                  <Typography variant="caption">
                    {new Date(c.timestamp).toLocaleString()} - {c.userEmail}
                  </Typography>
                </Paper>
              ))}
            </div>
          </Container>
        </Drawer>
    </Box>
  );
};

export default BKOverview;
